import { useState } from "react";
import {
  useCreate,
  useDelete,
  useListContext,
  useUpdate,
} from "ra-core";
import { useDelayedRefresh } from "./useDelayedRefresh";
import { Trash2, Plus, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type LineItem = {
  id: number;
  load_id: number;
  parent_type: string;
  parent_id: number;
  side: "buy" | "sell";
  description?: string;
  material_type?: string;
  packaging_type?: string;
  quantity?: number;
  unit?: string;
  estimated_weight?: number;
  actual_weight?: number;
  unit_price?: number;
  currency?: string;
  total_amount?: number;
  notes?: string;
  sort_order?: number;
};

export const MATERIAL_TYPE_OPTIONS = [
  "Academy Bikes", "B-Bales", "Baby Rummage", "General Trading",
  "Bed Bath & Beyond", "Belts", "Books", "Books - Consignment",
  "Border Rag", "Bric Brac", "Bill Caps", "Capsacks",
  "Christmas Bric Brac", "Coats and Jackets", "Commission - Internal Sale",
  "Cotton Yarn Waste", "Cotton Rags", "Credential Bric Brac",
  "Credential Shoes", "Credentials", "Dollar Store Showroom Samples",
  "Electronics", "Freight", "Fumigation", "Furniture", "Gaylords",
  "Graded Goods", "Halloween Product", "Hard Toys", "Housewares",
  "H&M Store Return Clothing", "Jeans", "KLS Store Return Toys", "Linens",
  "Backpacks", "LL Bean - Heavy Clothes", "LL Bean - Light Clothes",
  "Mixed Season Clothing", "Shoes Boots", "Misc. Charge", "Miscellaneous",
  "Mix (Tropical)", "Mix (Winter)", "Mixed Rag", "Mixed Toys",
  "Motorized Toys", "New-Overstock", "Premium", "Premium - Aftermarket",
  "Premium - QX", "Premium - Unopened Bags", "PCW Garments",
  "Pre consumer Clippings", "Pre Consumer Textile Scraps", "Purses",
  "Purses and Belts", "See Packing List", "Shirts and Blouses",
  "Shoes (Single)", "Shoes (Used)", "Skids", "Soft Toys", "Sporting Goods",
  "Sporting Shoes", "Storage", "Store Return Bikes", "Store Return Clothing",
  "Store Return Furniture", "Store Return Tools", "Store Returns",
  "Sweaters", "TShirts", "TRU Bikes", "TRU Toys", "TRU dotcom",
  "Used Bikes", "Vintage", "Waste of Man Made Fibers", "Wipers", "Wire Fee",
  "Worn Clothing 6309", "Worn Clothing 6309 - Services",
];

export const PACKAGING_TYPE_OPTIONS = [
  "Bales", "Boxes", "Bags", "Capsacks", "Container",
  "Gaylords", "Loose", "Service", "Skids", "Tons",
];

export const UNIT_OPTIONS = ["lbs", "kgs", "Each"];
export const CURRENCY_OPTIONS = ["USD", "CAD", "AUD", "GBP", "EUR"];

const EMPTY_ROW = {
  material_type: "",
  packaging_type: "Bales",
  quantity: "",
  unit: "lbs",
  estimated_weight: "",
  actual_weight: "",
  unit_price: "",
  currency: "USD",
  description: "",
};

type EditableRow = typeof EMPTY_ROW;

const Sel = ({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
}) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-7 text-sm min-w-0">
      <SelectValue placeholder={placeholder ?? "—"} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o} value={o} className="text-sm">
          {o}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const rowToLineItem = (values: EditableRow): Partial<LineItem> => {
  const qty = values.quantity ? Number(values.quantity) : undefined;
  const price = values.unit_price ? Number(values.unit_price) : undefined;
  return {
    material_type: values.material_type || undefined,
    packaging_type: values.packaging_type || undefined,
    quantity: qty,
    unit: values.unit || undefined,
    estimated_weight: values.estimated_weight
      ? Number(values.estimated_weight)
      : undefined,
    actual_weight: values.actual_weight
      ? Number(values.actual_weight)
      : undefined,
    unit_price: price,
    currency: values.currency || "USD",
    description: values.description || undefined,
  };
};

const itemToRow = (item: LineItem): EditableRow => ({
  material_type: item.material_type ?? "",
  packaging_type: item.packaging_type ?? "Bales",
  quantity: item.quantity != null ? String(item.quantity) : "",
  unit: item.unit ?? "lbs",
  estimated_weight:
    item.estimated_weight != null ? String(item.estimated_weight) : "",
  actual_weight: item.actual_weight != null ? String(item.actual_weight) : "",
  unit_price: item.unit_price != null ? String(item.unit_price) : "",
  currency: item.currency ?? "USD",
  description: item.description ?? "",
});

const EditableFields = ({
  values,
  set,
}: {
  values: EditableRow;
  set: (f: keyof EditableRow, v: string) => void;
}) => (
  <>
    <td className="p-1 min-w-36">
      <Sel
        value={values.material_type}
        onChange={(v) => set("material_type", v)}
        options={MATERIAL_TYPE_OPTIONS}
        placeholder="Material *"
      />
    </td>
    <td className="p-1 min-w-28">
      <Sel
        value={values.packaging_type}
        onChange={(v) => set("packaging_type", v)}
        options={PACKAGING_TYPE_OPTIONS}
        placeholder="Packaging *"
      />
    </td>
    <td className="p-1 w-20">
      <Sel
        value={values.unit}
        onChange={(v) => set("unit", v)}
        options={UNIT_OPTIONS}
        placeholder="Unit *"
      />
    </td>
    <td className="p-1 w-24">
      <Input
        value={values.quantity}
        onChange={(e) => set("quantity", e.target.value)}
        type="number"
        placeholder={values.unit === "Each" ? "# Pkgs *" : "# Pkgs"}
        className="h-7 text-sm"
      />
    </td>
    <td className="p-1 w-28">
      <Input
        value={values.unit_price}
        onChange={(e) => set("unit_price", e.target.value)}
        type="number"
        placeholder="Price *"
        className="h-7 text-sm"
      />
    </td>
    <td className="p-1 w-20">
      <Sel
        value={values.currency}
        onChange={(v) => set("currency", v)}
        options={CURRENCY_OPTIONS}
      />
    </td>
    <td className="p-1 w-24">
      <Input
        value={values.estimated_weight}
        onChange={(e) => set("estimated_weight", e.target.value)}
        type="number"
        placeholder="Est. Wt *"
        className="h-7 text-sm"
      />
    </td>
    <td className="p-1 w-24">
      <Input
        value={values.actual_weight}
        onChange={(e) => set("actual_weight", e.target.value)}
        type="number"
        placeholder="Act. Wt"
        className="h-7 text-sm"
      />
    </td>
    <td className="p-1">
      <Input
        value={values.description}
        onChange={(e) => set("description", e.target.value)}
        placeholder="Description"
        className="h-7 text-sm"
      />
    </td>
  </>
);

const LineItemRow = ({
  item,
  onDelete,
  onSave,
}: {
  item: LineItem;
  onDelete: (id: number) => void;
  onSave: (id: number, data: Partial<LineItem>) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<EditableRow>(() => itemToRow(item));
  const set = (field: keyof EditableRow, value: string) =>
    setValues((v) => ({ ...v, [field]: value }));

  const handleSave = () => {
    onSave(item.id, rowToLineItem(values));
    setEditing(false);
  };

  const handleCancel = () => {
    setValues(itemToRow(item));
    setEditing(false);
  };

  if (editing) {
    return (
      <tr className="border-b bg-muted/30">
        <EditableFields values={values} set={set} />
        <td className="p-1 text-right text-sm font-medium">
          {values.quantity && values.unit_price
            ? (Number(values.quantity) * Number(values.unit_price)).toFixed(2)
            : "—"}
        </td>
        <td className="p-1 w-16">
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleSave}>
              <Check className="h-3.5 w-3.5 text-green-600" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleCancel}>
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => setEditing(true)}>
      <td className="p-2 text-sm">{item.material_type || "—"}</td>
      <td className="p-2 text-sm">{item.packaging_type || "—"}</td>
      <td className="p-2 text-sm">{item.unit || "—"}</td>
      <td className="p-2 text-sm text-right">{item.quantity ?? "—"}</td>
      <td className="p-2 text-sm text-right">
        {item.unit_price != null ? Number(item.unit_price).toFixed(2) : "—"}
      </td>
      <td className="p-2 text-sm">{item.currency || "—"}</td>
      <td className="p-2 text-sm text-right">{item.estimated_weight ?? "—"}</td>
      <td className="p-2 text-sm text-right">{item.actual_weight ?? "—"}</td>
      <td className="p-2 text-sm text-muted-foreground">{item.description || ""}</td>
      <td className="p-2 text-sm text-right font-medium">
        {item.total_amount != null ? Number(item.total_amount).toFixed(2) : "—"}
      </td>
      <td className="p-2 w-10">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
        >
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </Button>
      </td>
    </tr>
  );
};

const NewItemRow = ({
  loadId,
  side,
  onCreated,
}: {
  loadId: number;
  side: "buy" | "sell";
  onCreated: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<EditableRow>({ ...EMPTY_ROW });
  const [create] = useCreate();
  const refresh = useDelayedRefresh();

  const set = (field: keyof EditableRow, value: string) =>
    setValues((v) => ({ ...v, [field]: value }));

  const handleSave = () => {
    if (!values.material_type) return;
    create(
      "line_items",
      {
        data: {
          load_id: loadId,
          parent_type: "load",
          parent_id: loadId,
          side,
          sort_order: 0,
          ...rowToLineItem(values),
        },
      },
      {
        onSuccess: () => {
          setValues({ ...EMPTY_ROW });
          setOpen(false);
          onCreated();
          refresh();
        },
      }
    );
  };

  if (!open) {
    return (
      <tr>
        <td colSpan={11} className="p-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground gap-1.5"
            onClick={() => setOpen(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add item
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b bg-muted/20">
      <EditableFields values={values} set={set} />
      <td className="p-1 text-right text-sm font-medium">
        {values.quantity && values.unit_price
          ? (Number(values.quantity) * Number(values.unit_price)).toFixed(2)
          : "—"}
      </td>
      <td className="p-1 w-16">
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleSave}>
            <Check className="h-3.5 w-3.5 text-green-600" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => { setValues({ ...EMPTY_ROW }); setOpen(false); }}
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

export const LineItemsTable = ({
  loadId,
  side,
  title,
}: {
  loadId: number;
  side: "buy" | "sell";
  title: string;
}) => {
  const { data = [], refetch } = useListContext<LineItem>();
  const [deleteOne] = useDelete();
  const [update] = useUpdate();
  const refresh = useDelayedRefresh();

  const items = data
    .filter((item) => item.side === side)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const total = items.reduce(
    (sum, item) => sum + (item.total_amount ? Number(item.total_amount) : 0),
    0
  );

  const handleDelete = (id: number) => {
    deleteOne(
      "line_items",
      { id, previousData: items.find((i) => i.id === id) },
      { onSuccess: () => { refetch(); refresh(); } }
    );
  };

  const handleSave = (id: number, patch: Partial<LineItem>) => {
    update(
      "line_items",
      { id, data: patch, previousData: items.find((i) => i.id === id) },
      { onSuccess: () => { refetch(); refresh(); } }
    );
  };

  return (
    <div className="overflow-x-auto">
      <h3 className="text-sm font-semibold mb-2">{title}</h3>
      <table className="w-full text-sm border-collapse min-w-[900px]">
        <thead>
          <tr className="border-b text-muted-foreground text-xs">
            <th className="text-left p-2 font-medium">Material *</th>
            <th className="text-left p-2 font-medium">Packaging *</th>
            <th className="text-left p-2 font-medium">Unit *</th>
            <th className="text-right p-2 font-medium w-24"># Pkgs *</th>
            <th className="text-right p-2 font-medium w-24">Price *</th>
            <th className="text-left p-2 font-medium w-24 whitespace-nowrap">Currency *</th>
            <th className="text-right p-2 font-medium w-24">Est. Wt *</th>
            <th className="text-right p-2 font-medium w-24">Act. Wt</th>
            <th className="text-left p-2 font-medium">Description</th>
            <th className="text-right p-2 font-medium">Total</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <LineItemRow
              key={item.id}
              item={item}
              onDelete={handleDelete}
              onSave={handleSave}
            />
          ))}
          <NewItemRow loadId={loadId} side={side} onCreated={refetch} />
        </tbody>
        {items.length > 0 && (
          <tfoot>
            <tr className="border-t">
              <td colSpan={9} className="p-2 text-right text-xs text-muted-foreground font-medium">
                Total
              </td>
              <td className="p-2 text-right font-semibold">
                {total.toFixed(2)}
              </td>
              <td />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

const EMPTY_QUICK = {
  material_type: "",
  packaging_type: "Bales",
  quantity: "",
  unit: "lbs",
  estimated_weight: "",
  actual_weight: "",
  buy_price: "",
  sell_price: "",
  currency: "USD",
  description: "",
};

type QuickRow = typeof EMPTY_QUICK;

export const QuickAddMaterial = ({
  loadId,
  onCreated,
}: {
  loadId: number;
  onCreated: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<QuickRow>({ ...EMPTY_QUICK });
  const [create] = useCreate();
  const [saving, setSaving] = useState(false);
  const refresh = useDelayedRefresh();

  const set = (field: keyof QuickRow, value: string) =>
    setValues((v) => ({ ...v, [field]: value }));

  const handleSave = () => {
    if (!values.material_type) return;
    setSaving(true);

    const shared = {
      load_id: loadId,
      parent_type: "load",
      parent_id: loadId,
      sort_order: 0,
      material_type: values.material_type || undefined,
      packaging_type: values.packaging_type || undefined,
      quantity: values.quantity ? Number(values.quantity) : undefined,
      unit: values.unit || undefined,
      estimated_weight: values.estimated_weight
        ? Number(values.estimated_weight)
        : undefined,
      actual_weight: values.actual_weight
        ? Number(values.actual_weight)
        : undefined,
      currency: values.currency || "USD",
      description: values.description || undefined,
    };

    const buyPrice = values.buy_price ? Number(values.buy_price) : undefined;
    const sellPrice = values.sell_price ? Number(values.sell_price) : undefined;

    create(
      "line_items",
      {
        data: {
          ...shared,
          side: "buy",
          unit_price: buyPrice,
        },
      },
      {
        onSuccess: () => {
          create(
            "line_items",
            {
              data: {
                ...shared,
                side: "sell",
                unit_price: sellPrice,
              },
            },
            {
              onSuccess: () => {
                setSaving(false);
                setValues({ ...EMPTY_QUICK });
                setOpen(false);
                onCreated();
                refresh();
              },
              onError: () => setSaving(false),
            }
          );
        },
        onError: () => setSaving(false),
      }
    );
  };

  if (!open) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => setOpen(true)}
      >
        <Plus className="h-3.5 w-3.5" />
        Quick Add to Both Sides
      </Button>
    );
  }

  return (
    <div className="border rounded-lg p-4 bg-muted/20">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Quick Add — Both Sides</h3>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6"
          onClick={() => { setValues({ ...EMPTY_QUICK }); setOpen(false); }}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <div className="col-span-2 sm:col-span-2">
          <label className="text-xs text-muted-foreground mb-1 block">Material *</label>
          <Sel
            value={values.material_type}
            onChange={(v) => set("material_type", v)}
            options={MATERIAL_TYPE_OPTIONS}
            placeholder="Select material..."
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Packaging *</label>
          <Sel
            value={values.packaging_type}
            onChange={(v) => set("packaging_type", v)}
            options={PACKAGING_TYPE_OPTIONS}
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Unit *</label>
          <Sel
            value={values.unit}
            onChange={(v) => set("unit", v)}
            options={UNIT_OPTIONS}
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">
            # Pkgs{values.unit === "Each" ? " *" : ""}
          </label>
          <Input
            value={values.quantity}
            onChange={(e) => set("quantity", e.target.value)}
            type="number"
            placeholder="—"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Est. Weight *</label>
          <Input
            value={values.estimated_weight}
            onChange={(e) => set("estimated_weight", e.target.value)}
            type="number"
            placeholder="—"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Act. Weight</label>
          <Input
            value={values.actual_weight}
            onChange={(e) => set("actual_weight", e.target.value)}
            type="number"
            placeholder="—"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Currency *</label>
          <Sel
            value={values.currency}
            onChange={(v) => set("currency", v)}
            options={CURRENCY_OPTIONS}
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-blue-600 mb-1 block">Buy Price *</label>
          <Input
            value={values.buy_price}
            onChange={(e) => set("buy_price", e.target.value)}
            type="number"
            placeholder="Vendor price"
            className="h-8 text-sm border-blue-200 focus-visible:ring-blue-300"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-green-600 mb-1 block">Sell Price *</label>
          <Input
            value={values.sell_price}
            onChange={(e) => set("sell_price", e.target.value)}
            type="number"
            placeholder="Customer price"
            className="h-8 text-sm border-green-200 focus-visible:ring-green-300"
          />
        </div>
        <div className="col-span-2 sm:col-span-4">
          <label className="text-xs text-muted-foreground mb-1 block">Description</label>
          <Input
            value={values.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Optional description"
            className="h-8 text-sm"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setValues({ ...EMPTY_QUICK }); setOpen(false); }}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || !values.material_type}
          className="gap-1.5"
        >
          <Check className="h-3.5 w-3.5" />
          Add to Buy &amp; Sell
        </Button>
      </div>
    </div>
  );
};
